import React, { useState, useEffect, useContext } from "react";
import {
    makeStyles,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    Chip,
    Typography,
    Button,
    Box,
    useTheme
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";
import { Edit as EditIcon, Add as AddIcon, Loyalty } from "@material-ui/icons";
import { toast } from "react-toastify";
import useResellerPlans from "../../hooks/useResellerPlans";
import { i18n } from "../../translate/i18n";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import ResellerPlanModal from "./ResellerPlanModal";

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        padding: theme.spacing(1, 0),
    },
    topBar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "14px 20px",
        marginBottom: theme.spacing(2),
        borderRadius: 12,
        backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
        border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
        boxShadow: theme.palette.type === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.05)",
    },
    topBarLeft: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
    },
    topBarTitle: {
        fontSize: "0.95rem",
        fontWeight: 700,
        letterSpacing: "0.5px",
        color: theme.palette.primary.main,
        textTransform: "uppercase",
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    topBarIcon: {
        color: theme.palette.primary.main,
        fontSize: "1.3rem",
    },
    countChip: {
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        fontWeight: 600,
        borderRadius: 13,
        height: 26,
        border: `1px solid ${theme.palette.primary.main}4D`,
        fontSize: "0.75rem",
        padding: "0 8px",
    },
    newButton: {
        borderRadius: 8,
        fontWeight: 700,
        fontSize: "0.88rem",
        textTransform: "none",
        padding: "8px 24px",
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: `${theme.palette.primary.contrastText || "#ffffff"} !important`,
        boxShadow: `0 0 14px ${theme.palette.primary.main}66`,
        transition: "all 0.25s ease",
        "&:hover": {
            backgroundColor: `${theme.palette.primary.main}CC !important`,
            boxShadow: `0 0 18px ${theme.palette.primary.main}99`,
        },
    },
    tableContainer: {
        width: '100%',
        borderRadius: 12,
        overflow: "hidden",
        backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
        border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
        boxShadow: theme.palette.type === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.05)",
        ...theme.scrollbarStyles,
    },
    table: {
        width: '100%',
    },
    tableHeader: {
        fontWeight: 700,
        fontSize: "0.78rem",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        backgroundColor: theme.palette.type === "dark" ? "#1b1e24 !important" : "#F1F5F9 !important",
        color: `${theme.palette.primary.main} !important`,
        borderBottom: `2px solid ${theme.palette.primary.main}4D`,
        padding: "12px 14px",
    },
    tableRow: {
        transition: "background-color 0.2s ease",
        "&:hover": {
            backgroundColor: theme.palette.type === "dark" ? "rgba(0, 229, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
        },
    },
    tableCell: {
        borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
        padding: "12px 14px",
        color: theme.palette.type === "dark" ? "#FFFFFF" : "#1E293B",
    },
    editButton: {
        color: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}14`,
        border: `1px solid ${theme.palette.primary.main}4D`,
        borderRadius: 8,
        padding: 6,
        transition: "all 0.2s ease",
        "&:hover": {
            backgroundColor: `${theme.palette.primary.main}26`,
            boxShadow: `0 0 10px ${theme.palette.primary.main}66`,
        },
    },
    priceText: {
        color: theme.palette.primary.main,
        fontWeight: 800,
        fontSize: "0.95rem",
    },
    balanceText: {
        color: "#25D366",
        fontWeight: 700,
        fontSize: "0.92rem",
    },
    discountChip: {
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        fontWeight: 700,
        border: `1px solid ${theme.palette.primary.main}4D`,
        borderRadius: 6,
        fontSize: "0.8rem",
    },
    emptyNotice: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: "rgba(255, 255, 255, 0.5)",
        fontSize: "0.95rem",
    }
}));

export function ResellerPlansManagerGrid({ records, onSelect, currency }) {
    const classes = useStyles();
    const theme = useTheme();
    const isLight = theme.palette.type === "light";

    return (
        <Paper className={classes.tableContainer} elevation={0}>
            <Table className={classes.table} size="small" aria-label="reseller plans table">
                <TableHead>
                    <TableRow>
                        <TableCell align="center" className={classes.tableHeader} style={{ width: '60px' }}>#</TableCell>
                        <TableCell align="left" className={classes.tableHeader} style={{ minWidth: 180 }}>{i18n.t("resellerPlans.grid.name")}</TableCell>
                        <TableCell align="center" className={classes.tableHeader} style={{ minWidth: 120 }}>{i18n.t("resellerPlans.grid.value")}</TableCell>
                        <TableCell align="center" className={classes.tableHeader} style={{ minWidth: 130 }}>{i18n.t("resellerPlans.grid.earnedBalance")}</TableCell>
                        <TableCell align="center" className={classes.tableHeader} style={{ minWidth: 120 }}>{i18n.t("resellerPlans.grid.discountPercentage")}</TableCell>
                        <TableCell align="center" className={classes.tableHeader} style={{ minWidth: 120 }}>{i18n.t("resellerPlans.grid.expiresIn")}</TableCell>
                        <TableCell align="center" className={classes.tableHeader} style={{ width: '80px' }}>{i18n.t("resellerPlans.grid.actions")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={7} className={classes.emptyNotice}>
                                No hay planes reseller registrados.
                            </TableCell>
                        </TableRow>
                    ) : (
                        records.map((row) => (
                            <TableRow key={row.id} className={classes.tableRow}>
                                <TableCell align="center" className={classes.tableCell} style={{ fontWeight: 600, color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>
                                    {row.id}
                                </TableCell>
                                <TableCell align="left" className={classes.tableCell}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700, color: isLight ? '#0F172A' : '#fff', fontSize: '0.92rem' }}>
                                        {row.name || '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" className={classes.tableCell}>
                                    <Typography className={classes.priceText}>
                                        {currency} {row.value ? parseFloat(row.value).toLocaleString('pt-br', { minimumFractionDigits: 2 }) : "0,00"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" className={classes.tableCell}>
                                    <Typography className={classes.balanceText}>
                                        {currency} {row.earnedBalance ? parseFloat(row.earnedBalance).toLocaleString('pt-br', { minimumFractionDigits: 2 }) : "0,00"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" className={classes.tableCell}>
                                    <Chip
                                        size="small"
                                        label={`${row.discountPercentage || 0}%`}
                                        className={classes.discountChip}
                                    />
                                </TableCell>
                                <TableCell align="center" className={classes.tableCell}>
                                    <Typography variant="body2" style={{ color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.85)' }}>
                                        {row.expiresIn} {i18n.t("resellerPlans.grid.days")}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" className={classes.tableCell}>
                                    <IconButton
                                        className={classes.editButton}
                                        onClick={() => onSelect(row)}
                                        aria-label="edit"
                                        size="small"
                                    >
                                        <EditIcon style={{ fontSize: "1.1rem" }} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </Paper>
    );
}

export default function ResellerPlansManager() {
    const classes = useStyles();
    const { list, save, update, remove } = useResellerPlans();
    const { getAll: getAllSettings } = useCompanySettings();
    const { user } = useContext(AuthContext);
    const [currency, setCurrency] = useState('USD $');

    const [planModalOpen, setPlanModalOpen] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [record, setRecord] = useState({
        name: "",
        value: 0,
        discountPercentage: 0,
        earnedBalance: 0,
        expiresIn: 365
    });

    useEffect(() => {
        async function fetchData() {
            await loadPlans();
            try {
                const settings = await getAllSettings(user.companyId);
                if (settings && settings.length > 0) {
                    const currencySetting = settings.find(s => s.key === 'system_currency');
                    if (currencySetting && currencySetting.value) {
                        setCurrency(currencySetting.value);
                    }
                } else if (settings && settings.system_currency) {
                    setCurrency(settings.system_currency);
                }
            } catch (err) {
                console.error("Error loading currency settings", err);
            }
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadPlans = async () => {
        setLoading(true);
        try {
            const planList = await list();
            setRecords(planList || []);
        } catch (e) {
            toast.error(i18n.t("resellerPlans.toasts.loadError", "Error al cargar los planes reseller"));
        }
        setLoading(false);
    };

    const handleOpenNew = () => {
        setRecord({
            name: "",
            value: 0,
            discountPercentage: 0,
            earnedBalance: 0,
            expiresIn: 365
        });
        setPlanModalOpen(true);
    };

    const handleSelect = (data) => {
        setRecord({
            id: data.id,
            name: data.name,
            value: data.value,
            discountPercentage: data.discountPercentage || 0,
            earnedBalance: data.earnedBalance || 0,
            expiresIn: data.expiresIn || 365
        });
        setPlanModalOpen(true);
    };

    const handleCloseModal = () => {
        setPlanModalOpen(false);
    };

    const handleSubmit = async (data) => {
        setLoading(true);
        try {
            if (data.id !== undefined) {
                await update(data);
            } else {
                await save(data);
            }
            await loadPlans();
            setPlanModalOpen(false);
            toast.success(i18n.t("resellerPlans.toasts.saveSuccess", "¡Plan Reseller guardado correctamente!"));
        } catch (e) {
            toast.error(i18n.t("resellerPlans.toasts.saveError", "Ocurrió un error al guardar el plan reseller"));
        }
        setLoading(false);
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await remove(record.id);
            await loadPlans();
            setShowConfirmDialog(false);
            setPlanModalOpen(false);
            toast.success(i18n.t("resellerPlans.toasts.deleteSuccess", "¡Plan Reseller eliminado!"));
        } catch (e) {
            toast.error(i18n.t("resellerPlans.toasts.deleteError", "Ocurrió un error al eliminar el plan reseller"));
        }
        setLoading(false);
    };

    const handleOpenDeleteDialog = (planToDelete) => {
        if (planToDelete) {
            setRecord(planToDelete);
        }
        setShowConfirmDialog(true);
    };

    return (
        <div className={classes.root}>
            {/* TOP BAR CON TÍTULO, CONTADOR Y BOTÓN + NUEVO */}
            <Paper className={classes.topBar} elevation={0}>
                <div className={classes.topBarLeft}>
                    <Typography className={classes.topBarTitle}>
                        <Loyalty className={classes.topBarIcon} />
                        {i18n.t("resellerPlans.title", "Planes Reseller del Sistema")}
                    </Typography>
                    <Chip
                        className={classes.countChip}
                        label={`${i18n.t("resellerPlans.totalPlans", "Planes Reseller Registrados")}: ${records.length}`}
                        size="small"
                    />
                </div>
                <Button
                    className={classes.newButton}
                    variant="contained"
                    startIcon={<AddIcon style={{ fontSize: "1.2rem", fontWeight: "bold" }} />}
                    onClick={handleOpenNew}
                >
                    {i18n.t("resellerPlans.newPlan", "Nuevo Plan Reseller")}
                </Button>
            </Paper>

            {/* TABLA DE PLANES RESELLER */}
            <ResellerPlansManagerGrid
                records={records}
                onSelect={handleSelect}
                currency={currency}
            />

            {/* MODAL FLOTANTE DE CREACIÓN / EDICIÓN */}
            <ResellerPlanModal
                open={planModalOpen}
                onClose={handleCloseModal}
                initialValue={record}
                onSubmit={handleSubmit}
                onDelete={handleOpenDeleteDialog}
                loading={loading}
                currency={currency}
            />

            {/* MODAL DE CONFIRMACIÓN DE ELIMINACIÓN */}
            <ConfirmationModal
                title={i18n.t("resellerPlans.deleteConfirm.title", "Eliminar Registro")}
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={handleDelete}
            >
                {i18n.t("resellerPlans.deleteConfirm.message", "¿Desea realmente eliminar este plan reseller?")}
            </ConfirmationModal>
        </div>
    );
}
